import React from 'react';
import pluralize from 'pluralize';

import { capitalize } from '../../../../../util/text';
import { formatPriceForDisplay } from '../../../../../util/format-money';
import { CURRENCY, CURRENCY_SYMBOLS } from '../../../../../constants/currency';
import ContextualString from '../../../../../components/contextual/ContextualString';

/**
 * Search subscription plan enum.
 *
 * @enum SEARCH_SUBSCRIPTION_PLAN
 * @type {{SHUTTERBUG_PLUS: string, SHUTTERBUG_PREMIUM: string}}
 */
export const SEARCH_SUBSCRIPTION_PLAN = {
  FREE: null,
  SHUTTERBUG_PLUS: 'SHUTTERBUG_PLUS',
  SHUTTERBUG_PREMIUM: 'SHUTTERBUG_PREMIUM',
};

export const SEARCH_SUBSCRIPTION_PLAN_PRIORITY = {
  [SEARCH_SUBSCRIPTION_PLAN.FREE]: 0,
  [SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS]: 1,
  [SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM]: 2,
};

export const SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD = {
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY',
};

/**
 * Hardcoded metadata  per SearchSubscription plan.
 */
export const searchSubscriptionMeta = {
  [SEARCH_SUBSCRIPTION_PLAN.FREE]: {
    accentColor: '#f1794e',
    label: 'Waldo Basic',
    shortLabel: 'Basic',
    features: [
      'Unlimited albums',
      'Unlimited invites & contributors',
      'Accessible on iOS, Android, and web',
      '10GB storage',
    ],
    featuresShort: ['Unlimited albums', '10GB storage'],
    badgeText: '',
  },
  [SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS]: {
    accentColor: '#f1794e',
    icon: '/diamond-orange.svg',
    colors: ['#c7e2ff', '#54c9f2', '#3385ed'],
    label: 'Waldo Plus',
    shortLabel: 'Plus',
    shortSubtitle:
      'Great for crowdsourcing photos & videos with friends & family!',
    features: [
      <span>
        Everything in <span className="text-extra-bold">FREE</span> and…
      </span>,
      'Automated photo delivery up to 10 people',
      'Watermark removal',
      'View & manage gallery access',
      'Live slideshows',
      '15% discount on prints',
      '200GB storage (total)',
    ],
    featuresShort: [
      'Automated photo delivery up to 10 people',
      '200GB storage',
      '15% discount on prints',
      'And more!',
    ],
    featuresWithTip: [
      {
        id: 'STORAGE',
        value: '200 GB of storage & unlimited events',
        tip: '',
      },
      {
        id: 'PHOTO_DELIVERY',
        value: 'AI-powered photo delivery for up to 10 family members',
        tip: 'No more hunting and pecking through albums! Waldo will text or push alert you when any of up to 10 faces added to your profile are found.',
      },
      {
        id: 'LIVE_SLIDESHOW',
        value: 'Automated slideshow builder',
        tip: "Waldo's AI-powered slideshow builder will turn your photos into beautiful, collage-style slides at the press of a button!",
      },
      {
        id: 'DISCOUNTS',
        value: '15% off special events, prints, and postcards',
        tip: '',
      },
      {
        id: 'PHOTO_MODERATION',
        value: 'Photo and member moderation',
        tip: 'Easily invite others to contribute photos and videos at your event while controlling who can add and view and which photos are published!',
      },
      {
        id: 'PHOTO_HOSTING',
        value: 'Photo and video hosting',
        tip: '',
      },
    ],
    onboardingFeatures: context => [
      {
        id: 'context-string',
        keyless: false,
        value: (
          <p className="text-extra-bold mb-0">
            All your{' '}
            <ContextualString
              id="identityReference"
              defaultMessage="person"
              type={context?.album_type}
              subtype={context?.album_subtype}
              formatter={contextualString => pluralize(contextualString, 2)}
            />{' '}
            photos automatically delivered!
          </p>
        ),
      },
      {
        id: 'plus',
        keyless: true,
        value: <p className="text-light mb-0 ml-3 text-sm">Plus...</p>,
      },
      {
        id: 'matched-photo-delivery',
        keyless: false,
        value: (
          <span>
            Matched photo delivery at other Waldofied events (up to{' '}
            <span className="text-extra-bold">10</span> profiles)
          </span>
        ),
      },
      'Unlimited photo & video sharing',
      'Private commenting & emoting',
      '200GB of storage',
      '15% discount on prints',
    ],
    badgeText: '',
  },
  [SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM]: {
    accentColor: '#0d84fe',
    icon: '/diamond-blue.svg',
    colors: ['#ffdaf5', '#e956c7', '#407be9'],
    label: 'Waldo Premium',
    shortLabel: 'Premium',
    features: [
      <span>
        Everything in <span className="text-extra-bold">PLUS</span> and…
      </span>,
      'Automated photo delivery up to 10 people',
      'Live slideshows',
      '15% discount on prints',
      '200GB storage (total)',
    ],
    featuresShort: [
      'Automated photo delivery up to 10 people',
      '200GB storage',
      '15% discount on prints',
      'And more!',
    ],
    onboardingFeatures: context => [
      {
        id: 'context-string',
        keyless: false,
        value: (
          <p className="text-extra-bold mb-0">
            All your{' '}
            <ContextualString
              id="identityReference"
              defaultMessage="person"
              type={context?.album_type}
              subtype={context?.album_subtype}
              formatter={contextualString => pluralize(contextualString, 2)}
            />{' '}
            photos automatically delivered!
          </p>
        ),
      },
      {
        id: 'plus',
        keyless: true,
        value: <p className="text-light mb-0 ml-3 text-sm">Plus...</p>,
      },
      {
        id: 'matched-photo-delivery',
        keyless: false,
        value: (
          <span>
            Matched photo delivery at other Waldofied events (up to{' '}
            <span className="text-extra-bold">10</span> profiles)
          </span>
        ),
      },
      'Unlimited photo & video sharing',
      'Private commenting & emoting',
      '200GB of storage',
      '15% discount on prints',
    ],
    badgeText: '',
  },
};

/**
 * Payment processor enum.
 *
 * @enum PAYMENT_PROCESSOR
 * @type {{APP_STORE: string, STRIPE: string, FREE: string}}
 */
export const PAYMENT_PROCESSOR = {
  APP_STORE: 'APP_STORE',
  FREE: 'FREE',
  STRIPE: 'STRIPE',
};

/**
 * Returns a title and label for a SearchSubscriptionItem based on its plan.
 *
 * @param {Object} searchSubscriptionItem The SearchSubscriptionItem.
 * @returns {{label: *, title: *}|{label: string, title: string}}
 */
export const getSearchSubscriptionItemLabels = searchSubscriptionItem => {
  if (!searchSubscriptionItem || !searchSubscriptionItem.plan) {
    return {
      title: '',
      label: '',
    };
  }

  const [title, label] = searchSubscriptionItem.plan.split('_').reverse();

  return {
    title: capitalize(title),
    label: capitalize(label),
  };
};

/**
 * Returns a price label for a SearchSubscriptionItem, ie "$2.99 / year".
 *
 * @param {Object} searchSubscriptionItem The SearchSubscriptionItem.
 * @returns {string}
 */
export const getSearchSubscriptionItemPriceLabel = searchSubscriptionItem => {
  if (!searchSubscriptionItem) {
    return '';
  }

  const price = formatPriceForDisplay(searchSubscriptionItem.price.value);
  const currency = CURRENCY_SYMBOLS[searchSubscriptionItem.price.currency];

  return `${currency}${price}`;
};

/**
 * Checks if an account is eligible for a free trial on the Shuttebrug subscription offerings.
 *
 * @param {Object} account The Account.
 * @returns {boolean}
 */
export const isEligibleForShutterbugFreeTrial = account => {
  return !!account?.search_subscription_free_trial_enabled;
};

/**
 * Determines whether the SearchSubscription is active trial.
 *
 * @param {Object} searchSubscription The SearchSubscription.
 * @returns {boolean}
 */
export const isSearchSubscriptionActiveTrial = searchSubscription => {
  if (!searchSubscription) {
    return false;
  }

  return searchSubscription.trial_days > 0;
};

export const getFreePlan = () => {
  return {
    id: null,
    plan: null,
    price: {
      value: 0,
      currency: CURRENCY.USD,
    },
  };
};
